import { fmtMoney } from "@/lib/poFormat";
import { fmtSqft, lineSqft, lineTotal } from "@/lib/poPricing";

// Letter-size purchase order for paper / "Save as PDF". Lives inside #print-area, so it
// only appears when the browser prints (the print stylesheet hides everything else).
const lineView = (l) => {
  if (l.kind === "sink") {
    return {
      name: [l.brand, l.model].filter(Boolean).join(" ") || "Sink",
      detail: [l.sink_type, l.size].filter(Boolean).join(" · "),
    };
  }
  return {
    name: l.slab_color || "Slab",
    detail: [
      l.material,
      l.finish,
      l.thickness,
      l.length_in && l.width_in ? `${l.length_in}" x ${l.width_in}"` : "",
    ]
      .filter(Boolean)
      .join(" · "),
  };
};

const Block = ({ label, lines }) => (
  <div>
    <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-zinc-500">
      {label}
    </div>
    {lines.filter(Boolean).map((x, i) => (
      <div key={i} className={i === 0 ? "font-semibold" : ""}>
        {x}
      </div>
    ))}
  </div>
);

export const POPrintSheet = ({ po, supplier, company, logoUrl }) => {
  const cur = po.currency || "USD";
  const items = po.lines.reduce((n, l) => n + lineTotal(l), 0);
  const charges = po.additional_charges || [];
  const chargesTotal = charges.reduce((n, c) => n + (Number(c.amount) || 0), 0);
  return (
    <div
      className="print-sheet bg-white p-8 text-[11px] leading-relaxed text-black"
      data-testid="po-print-sheet"
    >
      <div className="flex items-start justify-between border-b-2 border-black pb-4">
        <div>
          {logoUrl && (
            <img
              src={logoUrl}
              alt=""
              data-testid="po-print-logo"
              className="print-fixed print-fixed-logo mb-2 h-10 w-auto object-contain"
            />
          )}
          <div className="text-base font-bold">{company?.name || "Purchase order"}</div>
          {company?.address && <div className="whitespace-pre-line">{company.address}</div>}
          {company?.phone && <div>TEL: {company.phone}</div>}
          {company?.email && <div>{company.email}</div>}
        </div>
        <div className="text-right">
          <div className="text-2xl font-black tracking-tight">PURCHASE ORDER</div>
          <div className="text-sm font-semibold" data-testid="po-print-code">
            {po.code}
          </div>
          <div className="mt-1">Order date: {po.order_date || "—"}</div>
          {po.expected_date && <div>Expected: {po.expected_date}</div>}
          {po.reference && <div>Reference: {po.reference}</div>}
          <div>Status: {po.status}</div>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-6">
        <Block
          label="Supplier"
          lines={[
            po.supplier_name || supplier?.name || "No supplier",
            supplier?.contact,
            supplier?.rep_name
              ? `Attn: ${[supplier.rep_name, supplier.rep_phone].filter(Boolean).join(" · ")}`
              : "",
            supplier?.address,
            supplier?.phone,
            supplier?.email,
          ]}
        />
        <Block
          label="Ship to"
          lines={[company?.name, company?.address, company?.phone, company?.email]}
        />
        <Block
          label="Terms"
          lines={[
            po.payment_terms || "As agreed",
            `Currency: ${cur}`,
            po.consignment ? "Consignment stock" : "",
            po.customer_name ? `For ${po.customer_name}` : "",
            po.job_code ? `Job ${po.job_code}` : "",
          ]}
        />
      </div>

      <table className="mt-6 w-full border-collapse text-left">
        <thead>
          <tr className="border-b border-black text-[9px] uppercase tracking-[0.2em] text-zinc-500">
            <th className="py-2 pr-2">Item</th>
            <th className="py-2 pr-2">Details</th>
            <th className="py-2 pr-2 text-right">Qty</th>
            <th className="py-2 pr-2 text-right">Unit</th>
            <th className="py-2 text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {po.lines.map((l) => {
            const v = lineView(l);
            const sq = lineSqft(l);
            return (
              <tr key={l.line_id} className="border-b border-zinc-300 align-top">
                <td className="py-2 pr-2 font-semibold">{v.name}</td>
                <td className="py-2 pr-2">{v.detail}</td>
                <td className="py-2 pr-2 text-right">
                  {l.qty}
                  {sq && (
                    <div className="whitespace-nowrap text-[9px] text-zinc-600">
                      {fmtSqft(sq.sqft)} {sq.basis}
                    </div>
                  )}
                </td>
                <td className="py-2 pr-2 text-right">
                  {l.unit_cost == null || l.unit_cost === "" ? "—" : fmtMoney(l.unit_cost, cur)}
                  {sq && l.unit_cost != null && l.unit_cost !== "" ? "/ft²" : ""}
                </td>
                <td className="py-2 text-right">{fmtMoney(lineTotal(l), cur)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="mt-4 ml-auto w-72">
        <div className="flex justify-between py-0.5">
          <span>Items</span>
          <span>{fmtMoney(items, cur)}</span>
        </div>
        {charges.map((c) => (
          <div key={c.id || c.label} className="flex justify-between py-0.5">
            <span>{c.label || "Charge"}</span>
            <span>{fmtMoney(c.amount, cur)}</span>
          </div>
        ))}
        <div className="mt-1 flex justify-between border-t-2 border-black pt-1 text-sm font-black">
          <span>Order total</span>
          <span data-testid="po-print-total">{fmtMoney(items + chargesTotal, cur)}</span>
        </div>
      </div>

      {po.notes && (
        <div className="mt-5">
          <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-zinc-500">Notes</div>
          <div className="whitespace-pre-wrap">{po.notes}</div>
        </div>
      )}
      <p className="mt-6 text-[10px] text-zinc-600">
        Please confirm receipt of this purchase order and your expected ship date. Quote our
        PO number on all paperwork and labels.
      </p>
    </div>
  );
};
